import { createSlice } from '@reduxjs/toolkit';
import { ParentOrganization } from './models/parent-organization.model';
import parentOrganizationsService from '../services/parentOrganizationsService';
import * as uiSlice from './ui.slice';

interface ParentOrganizationsState {
  parentOrganizations: ParentOrganization[];
  loadingParentOrganizations: boolean;
  loadedParentOrganizations: boolean;
  loadingCurrentOrganization: boolean;
  loadedCurrentOrganization: boolean;
  currentOrganization: ParentOrganization | null;
  savingOrganization: boolean;
  savedOrganization: boolean;
}

export const initialState: ParentOrganizationsState = {
  parentOrganizations: [],
  loadingParentOrganizations: false,
  loadedParentOrganizations: false,
  loadingCurrentOrganization: false,
  loadedCurrentOrganization: false,
  currentOrganization: null,
  savingOrganization: false,
  savedOrganization: false,
};

export const slice = createSlice({
  name: 'parentOrganization',
  initialState,
  reducers: {
    setParentOrganizations: (
      state: ParentOrganizationsState,
      action: { type: string; payload: ParentOrganization[] },
    ) => {
      state.parentOrganizations = action.payload;
    },
    setLoadingParentOrganizations: (
      state: ParentOrganizationsState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingParentOrganizations = action.payload;
    },
    setLoadedParentOrganizations: (
      state: ParentOrganizationsState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedParentOrganizations = action.payload;
    },
    setLoadingCurrentOrganization: (
      state: ParentOrganizationsState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentOrganization = action.payload;
    },
    setLoadedCurrentOrganization: (
      state: ParentOrganizationsState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentOrganization = action.payload;
    },
    setCurrentOrganization: (
      state: ParentOrganizationsState,
      action: { type: string; payload: ParentOrganization | null },
    ) => {
      state.currentOrganization = action.payload;
    },
    setSavingOrganization: (
      state: ParentOrganizationsState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingOrganization = action.payload;
    },
    setSavedOrganization: (
      state: ParentOrganizationsState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedOrganization = action.payload;
    },
  },
});

export const { setParentOrganizations } = slice.actions;
export const { setLoadingParentOrganizations } = slice.actions;
export const { setLoadedParentOrganizations } = slice.actions;
export const { setLoadingCurrentOrganization } = slice.actions;
export const { setLoadedCurrentOrganization } = slice.actions;
export const { setCurrentOrganization } = slice.actions;
export const { setSavingOrganization } = slice.actions;
export const { setSavedOrganization } = slice.actions;

export const selectParentOrganizations = (state: {
  parentOrganizations: ParentOrganizationsState;
}) => state.parentOrganizations.parentOrganizations;

export const selectCurrentOrganization = (state: {
  parentOrganizations: ParentOrganizationsState;
}) => state.parentOrganizations.currentOrganization;

export const selectLoadingCurrentOrganization = (state: {
  parentOrganizations: ParentOrganizationsState;
}) => state.parentOrganizations.loadingCurrentOrganization;

export const selectSavingOrganization = (state: {
  parentOrganizations: ParentOrganizationsState;
}) => state.parentOrganizations.savingOrganization;

export const fetchParentOrganizationsAsync = () => (dispatch: Function) => {
  dispatch(setLoadingParentOrganizations(true));
  dispatch(setLoadedParentOrganizations(false));
  parentOrganizationsService.getParentOrganizations().subscribe({
    next: (organizations) => {
      dispatch(setParentOrganizations(organizations));
      dispatch(setLoadingParentOrganizations(false));
      dispatch(setLoadedParentOrganizations(true));
    },
    error: (err) => {
      dispatch(setLoadingParentOrganizations(false));
      dispatch(setLoadedParentOrganizations(false));
    },
  });
};

export const fetchOrganizationAsync = (id: string) => (dispatch: Function) => {
  dispatch(setLoadingCurrentOrganization(true));
  dispatch(setLoadedCurrentOrganization(false));
  dispatch(setCurrentOrganization(null));
  parentOrganizationsService.getParentOrganization(id).subscribe({
    next: (organization) => {
      dispatch(setCurrentOrganization(organization));
      dispatch(setLoadingCurrentOrganization(false));
      dispatch(setLoadedCurrentOrganization(true));
    },
    error: (err) => {
      dispatch(setLoadingCurrentOrganization(false));
      dispatch(setLoadedCurrentOrganization(false));
    },
  });
};

export const createOrganizationAsync =
  (organization: ParentOrganization) => (dispatch: Function) => {
    dispatch(setSavingOrganization(true));
    dispatch(setSavedOrganization(false));
    dispatch(setCurrentOrganization(null));
    parentOrganizationsService.createOrganization(organization).subscribe({
      next: (organization) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createOrganizationSuccess'),
        );
        dispatch(fetchParentOrganizationsAsync());
        dispatch(setCurrentOrganization(organization));
        dispatch(setSavingOrganization(false));
        dispatch(setSavedOrganization(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingOrganization(false));
        dispatch(setSavedOrganization(true));
      },
    });
  };

export const updateOrganizationAsync =
  (organization: ParentOrganization) => (dispatch: Function) => {
    dispatch(setSavingOrganization(true));
    dispatch(setSavedOrganization(false));
    parentOrganizationsService.updateOrganization(organization).subscribe({
      next: (organization) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updateOrganizationSuccess'),
        );
        dispatch(fetchParentOrganizationsAsync());
        dispatch(setCurrentOrganization(organization));
        dispatch(setSavingOrganization(false));
        dispatch(setSavedOrganization(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingOrganization(false));
        dispatch(setSavedOrganization(false));
      },
    });
  };

export default slice.reducer;
